import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';
import { createSignupCompletionNotifier } from './signupCompletion';

const AuthContext = createContext({
  user: null,
  isLoading: true,
  isAdmin: false,
});

const decodeJwtPayload = (token) => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (err) {
    return null;
  }
};

const hasAdminRole = (user, session) => {
  if (!user) return false;
  const tokenPayload = decodeJwtPayload(session?.access_token);
  const roles =
    tokenPayload?.app_metadata?.roles ||
    user.app_metadata?.roles ||
    user.user_metadata?.roles ||
    [];
  if (Array.isArray(roles)) {
    return roles.includes('admin');
  }
  if (typeof roles === 'string') {
    return roles === 'admin';
  }
  const singleRole =
    user.app_metadata?.role || user.user_metadata?.role || null;
  return singleRole === 'admin';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let authRevision = 0;
    const completion = createSignupCompletionNotifier();
    const applySession = (nextSession) => {
      if (!mounted) return;
      setSession(nextSession || null);
      setUser(nextSession?.user || null);
      setIsLoading(false);
    };
    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (!mounted) return;
        authRevision += 1;
        applySession(nextSession);
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || !nextSession) {
          completion.schedule(nextSession);
        }
      }
    );
    const init = async () => {
      const revision = authRevision;
      try {
        // The configured client handles URL detection; do not strip the callback
        // URL or race a second exchange against its initialization.
        const { data, error } = await supabase.auth.getSession();
        if (!mounted || revision !== authRevision) return;
        if (error) throw error;
        applySession(data.session);
        completion.schedule(data.session);
      } catch (_) {
        if (mounted && revision === authRevision) {
          applySession(null);
        }
      }
    };

    init();

    return () => {
      mounted = false;
      completion.dispose();
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAdmin: hasAdminRole(user, session),
      signOut: () => supabase.auth.signOut(),
      signInWithProvider: (provider) =>
        supabase.auth.signInWithOAuth({
          provider,
          options: { redirectTo: window.location.origin },
        }),
    }),
    [user, session, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
